import React, { useState } from "react";
import {
  Box,
  Collapse,
  Grid,
  IconButton,
  Typography,
  styled,
} from "@mui/material";
import IconButtons from "../../components/IconButtons/IconButtons";
import {
  dissociate_active_icon,
  dissociate_disabled_icon,
  delete_active_icon,
  delete_disabled_icon,
  edit_active_icon,
  edit_disabled_icon,
  listing_icon,
  right_arrow_active_icon,
  plus_active_icon,
  plus_disabled_icon,
  dissociate_default_icon,
  dissociate_default_disabled_icon,
  associate_default_icon,
  associate_default_disabled_icon
} from "../../constants/icons";
import {
  font_13,
  getBootcampStatus,
  getDate,
  hasAnyPermission,
} from "../../utils/utils";
import { useNavigate } from "react-router";
import NoDataAvailable from "../../components/NoDataAvailable/NoDataAvailable";
import BootstrapTooltip from "../../components/BootstrapTooltip/BootstrapTooltip";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { HelpTexts } from "../../constants/constants";
import { Link } from "react-router-dom";

const { ACTIVE_MODULE, CONCLUDED_MODULE, CONCLUDED_BOOTCAMP } = HelpTexts;

// Helper function to get module state flags
const getModuleStateFlags = (mod, bootcamp) => {
  return {
    moduleIsActive: mod?.status === "ACTIVE",
    moduleIsConcluded: mod?.status === "COMPLETED",
    moduleIsCancelled: mod?.status === "CANCELLED",
    bootcampIsCancelled: bootcamp?.status === "CANCELLED",
  };
};

// Helper function to get permission flags
const getPermissionFlags = (mod, isHr) => {
  const permission = mod?.permission;
  return {
    isModuleNSessionListAllowed: isHr || hasAnyPermission(permission),
    isAttendeeListAllowed: mod?.permission?.spoc || isHr,
    isSpoc: mod?.permission?.spoc || isHr,
  };
};

// Helper function to get disabled tooltip
const getDisabledTooltip = (
  isBootcampConcluded,
  moduleIsConcluded,
  moduleIsActive
) => {
  if (isBootcampConcluded) return CONCLUDED_BOOTCAMP;
  if (moduleIsConcluded) return CONCLUDED_MODULE;
  if (moduleIsActive) return ACTIVE_MODULE;
  return null;
};

// Component for Module Header with Status Badge
const ModuleHeader = ({ moduleStatus }) => {
  return (
    <Box
      className="sub-module-left-head-container"
      sx={{
        backgroundColor: moduleStatus?.color,
        color: moduleStatus?.text,
      }}
    >
      <Typography
        variant="font_15_bold"
        fontWeight={500}
        className="sub-module-left-head sub-module-left-head-nowrap"
      >
        {moduleStatus?.name}
      </Typography>
    </Box>
  );
};

// Component for Module Name with Link
const ModuleName = ({ mod, bootcamp, isModuleNSessionListAllowed }) => {
  if (!isModuleNSessionListAllowed) {
    return (
      <Typography
        variant="font_12_bold"
        color="secondary"
        className="sub-module-head"
      >
        {mod?.name}
      </Typography>
    );
  }

  return (
    <BootstrapTooltip title="View Module">
      <Typography
        variant="font_12_bold"
        color="secondary"
        className="sub-module-head"
      >
        <Link to={`/bootcamp/${bootcamp?.id}/module/${mod?.id}`}>
          {mod?.name}
        </Link>
      </Typography>
    </BootstrapTooltip>
  );
};

// Component for Action Buttons Group
const ActionButtons = ({
  mod,
  bootcamp,
  isSpoc,
  isBootcampConcluded,
  moduleIsConcluded,
  moduleIsActive,
  moduleIsCancelled,
  bootcampIsCancelled,
  isModuleNSessionListAllowed,
  handleListAssessments,
  handleEditBootcampModule,
  handleDeleteModalOpen,
  handleDissociateModalOpen,
  handleAssociateBootcampModalOpen,
  navigate,
}) => {
  const showSpocButtons = isSpoc && !(moduleIsCancelled || bootcampIsCancelled);

  return (
    <Box className="sub-module-buttons">
      {isSpoc && (
        <IconButtons
          width={font_13}
          height={font_13}
          image={listing_icon}
          handleClick={(e) => handleListAssessments(e, bootcamp, mod)}
          classList="margin-right-1"
          tooltip="List Assessments"
        />
      )}
      {showSpocButtons && (
        <>
          <IconButtons
            width={font_13}
            height={font_13}
            image={edit_active_icon}
            handleClick={() => handleEditBootcampModule(bootcamp, mod)}
            classList="margin-right-1"
            tooltip="Edit Module"
            disabledImage={edit_disabled_icon}
            disabled={isBootcampConcluded || moduleIsConcluded}
            disabledTooltip={getDisabledTooltip(
              isBootcampConcluded,
              moduleIsConcluded,
              false
            )}
          />
          <IconButtons
            width={font_13}
            height={font_13}
            image={associate_default_icon}
            handleClick={(e) =>
              handleAssociateBootcampModalOpen(e, bootcamp, mod)
            }
            classList="margin-right-1"
            tooltip="Associate Bootcamp"
            disabledImage={associate_default_disabled_icon}
            disabled={isBootcampConcluded || moduleIsConcluded}
            disabledTooltip={
              isBootcampConcluded
                ? CONCLUDED_BOOTCAMP
                : moduleIsConcluded
                ? CONCLUDED_MODULE
                : null
            }
          />
          <IconButtons
            width={font_13}
            height={font_13}
            image={delete_active_icon}
            handleClick={(e) => handleDeleteModalOpen(e, mod, "module")}
            tooltip="Delete Module"
            classList="margin-right-1"
            disabledImage={delete_disabled_icon}
            disabled={
              isBootcampConcluded || moduleIsConcluded || moduleIsActive
            }
            disabledTooltip={getDisabledTooltip(
              isBootcampConcluded,
              moduleIsConcluded,
              moduleIsActive
            )}
          />
          {mod?.bootcamps?.totalResults > 1 && (
            <IconButtons
              width={font_13}
              height={font_13}
              image={dissociate_default_icon}
              handleClick={(e) => handleDissociateModalOpen(e, mod, bootcamp)}
              tooltip="Dissociate Module"
              classList="margin-right-1"
              disabledImage={dissociate_default_disabled_icon}
              disabled={
                isBootcampConcluded || moduleIsConcluded || moduleIsActive
              }
              disabledTooltip={getDisabledTooltip(
                isBootcampConcluded,
                moduleIsConcluded,
                moduleIsActive
              )}
            />
          )}
        </>
      )}
      {isModuleNSessionListAllowed && (
        <IconButtons
          width={font_13}
          height={font_13}
          image={right_arrow_active_icon}
          tooltip="View Module"
          handleClick={() => {
            navigate(`/bootcamp/${bootcamp?.id}/module/${mod?.id}`);
          }}
        />
      )}
    </Box>
  );
};

// Component for Module Description with Expand/Collapse
const ModuleDescription = ({
  mod,
  viewMore,
  viewMoreId,
  handleViewMoreClick,
}) => {
  const wordCount = mod?.description?.length;
  const countLines = mod?.description?.split(/\r|\r\n|\n/);
  const lineLength = countLines?.length;
  const shouldShowExpand = wordCount > 200 || lineLength > 3;
  const isExpanded = viewMore && viewMoreId === mod?.id;

  const ExpandMore = styled((props) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
  })(({ theme, expand }) => ({
    transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
    marginLeft: "auto",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest,
    }),
  }));

  return (
    <Grid item md={5.46} className="sub-modal-cells">
      <Typography variant="font_11_bold" className="sub-modal-cell-subtitle">
        Description
      </Typography>
      <Collapse
        className={!shouldShowExpand ? "adjusted-height" : null}
        in={isExpanded}
        collapsedSize={shouldShowExpand ? 50 : "auto"}
      >
        <Typography
          variant="font_11_bold"
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {mod?.description}
        </Typography>
      </Collapse>
      {shouldShowExpand && (
        <Box
          onClick={() => handleViewMoreClick(mod?.id)}
          className="expand-more"
        >
          <Typography
            variant="font_9_bold"
            color="secondary"
            cursor="pointer"
            sx={{ fontWeight: 600 }}
          >
            {isExpanded ? "VIEW LESS" : "VIEW MORE"}
          </Typography>
          <ExpandMore
            expand={isExpanded}
            sx={{ padding: 0, verticalAlign: "sub" }}
          >
            <ExpandMoreIcon
              color="secondary"
              sx={{
                fontSize: "1rem",
              }}
            />
          </ExpandMore>
        </Box>
      )}
    </Grid>
  );
};

// Component for Attendee Count Cell
const AttendeeCountCell = ({
  mod,
  bootcamp,
  attendeeCountForBootcamp,
  isAttendeeListAllowed,
  navigate,
}) => {
  return (
    <Grid item md={2.16} className="sub-modal-cells">
      <Typography variant="font_11_bold" className="sub-modal-cell-subtitle">
        Current Bootcamp's Attendees
      </Typography>
      <Box>
        {isAttendeeListAllowed && attendeeCountForBootcamp ? (
          <Typography
            variant="font_11_bold"
            color="secondary"
            onClick={() => {
              navigate(`/bootcamp/${bootcamp?.id}/module/${mod?.id}/attendees`);
            }}
            className="cursor-pointer"
          >
            <u>
              <BootstrapTooltip title="Show Attendees">
                <span>{attendeeCountForBootcamp}</span>
              </BootstrapTooltip>
            </u>
          </Typography>
        ) : (
          <Typography variant="font_11_bold" component="span">
            {attendeeCountForBootcamp}
          </Typography>
        )}
      </Box>
    </Grid>
  );
};

// Component for Sessions Count Cell
const SessionsCountCell = ({
  mod,
  bootcamp,
  isModuleNSessionListAllowed,
  navigate,
}) => {
  return (
    <Grid item md={1.46} className="sub-modal-cells">
      <Typography variant="font_11_bold" className="sub-modal-cell-subtitle">
        Total Sessions
      </Typography>
      <Typography
        color={isModuleNSessionListAllowed && "secondary"}
        variant="font_11_bold"
        {...(isModuleNSessionListAllowed && {
          onClick: () => {
            navigate(`/bootcamp/${bootcamp?.id}/module/${mod?.id}/sessions`);
          },
        })}
        className={isModuleNSessionListAllowed ? "cursor-pointer" : ""}
      >
        {isModuleNSessionListAllowed ? (
          <u>
            <BootstrapTooltip title="Show Sessions">
              {mod?.sessions?.totalResults}
            </BootstrapTooltip>
          </u>
        ) : (
          mod?.sessions?.totalResults
        )}
      </Typography>
    </Grid>
  );
};

// Component for Module Details Grid
const ModuleDetailsGrid = ({
  mod,
  bootcamp,
  attendeeCountForBootcamp,
  isAttendeeListAllowed,
  isModuleNSessionListAllowed,
  viewMore,
  viewMoreId,
  handleViewMoreClick,
  navigate,
}) => {
  return (
    <Grid container gap={0} spacing={0} className="sub-modal-cells-container">
      <ModuleDescription
        mod={mod}
        viewMore={viewMore}
        viewMoreId={viewMoreId}
        handleViewMoreClick={handleViewMoreClick}
      />
      <Grid item md={1.46} className="sub-modal-cells">
        <Typography variant="font_11_bold" className="sub-modal-cell-subtitle">
          Start Date
        </Typography>
        <Typography variant="font_11_bold">
          {getDate(mod?.startDate)}
        </Typography>
      </Grid>
      <Grid item md={1.46} className="sub-modal-cells">
        <Typography variant="font_11_bold" className="sub-modal-cell-subtitle">
          End Date
        </Typography>
        <Typography variant="font_11_bold">{getDate(mod?.endDate)}</Typography>
      </Grid>
      <AttendeeCountCell
        mod={mod}
        bootcamp={bootcamp}
        attendeeCountForBootcamp={attendeeCountForBootcamp}
        isAttendeeListAllowed={isAttendeeListAllowed}
        navigate={navigate}
      />
      <SessionsCountCell
        mod={mod}
        bootcamp={bootcamp}
        isModuleNSessionListAllowed={isModuleNSessionListAllowed}
        navigate={navigate}
      />
    </Grid>
  );
};

// Component for Single Module Row
const ModuleRow = ({
  mod,
  bootcamp,
  index,
  expandBootcampId,
  isHr,
  isBootcampConcluded,
  handleDeleteModalOpen,
  handleDissociateModalOpen,
  handleEditBootcampModule,
  handleListAssessments,
  handleAssociateBootcampModalOpen,
  viewMore,
  viewMoreId,
  handleViewMoreClick,
  navigate,
}) => {
  const stateFlags = getModuleStateFlags(mod, bootcamp);
  const permissionFlags = getPermissionFlags(mod, isHr);
  const attendeeCountForBootcamp = mod?.batchAttendees?.[expandBootcampId] || 0;
  const moduleStatus = getBootcampStatus(mod?.status);

  return (
    <Box className="expanded-module-container" key={index}>
      <ModuleHeader moduleStatus={moduleStatus} />
      <Box
        className={`sub-module-middle-container sub-module-${moduleStatus?.name.toLowerCase()}`}
      >
        <Box className="sub-module-head-container">
          <Box className="sub-module-heading">
            <ModuleName
              mod={mod}
              bootcamp={bootcamp}
              isModuleNSessionListAllowed={
                permissionFlags.isModuleNSessionListAllowed
              }
            />
          </Box>
          <ActionButtons
            mod={mod}
            bootcamp={bootcamp}
            isSpoc={permissionFlags.isSpoc}
            isBootcampConcluded={isBootcampConcluded}
            moduleIsConcluded={stateFlags.moduleIsConcluded}
            moduleIsActive={stateFlags.moduleIsActive}
            moduleIsCancelled={stateFlags.moduleIsCancelled}
            bootcampIsCancelled={stateFlags.bootcampIsCancelled}
            isModuleNSessionListAllowed={
              permissionFlags.isModuleNSessionListAllowed
            }
            handleListAssessments={handleListAssessments}
            handleEditBootcampModule={handleEditBootcampModule}
            handleDeleteModalOpen={handleDeleteModalOpen}
            handleDissociateModalOpen={handleDissociateModalOpen}
            handleAssociateBootcampModalOpen={handleAssociateBootcampModalOpen}
            navigate={navigate}
          />
        </Box>
        <ModuleDetailsGrid
          mod={mod}
          bootcamp={bootcamp}
          attendeeCountForBootcamp={attendeeCountForBootcamp}
          isAttendeeListAllowed={permissionFlags.isAttendeeListAllowed}
          isModuleNSessionListAllowed={
            permissionFlags.isModuleNSessionListAllowed
          }
          viewMore={viewMore}
          viewMoreId={viewMoreId}
          handleViewMoreClick={handleViewMoreClick}
          navigate={navigate}
        />
      </Box>
    </Box>
  );
};

const BootcampModuleExpand = ({
  bootcamp,
  modules,
  handleDeleteModalOpen,
  handleDissociateModalOpen,
  handleEditBootcampModule,
  isHr,
  expandBootcampId,
  isBootcampConcluded,
  handleListAssessments,
  handleAssociateBootcampModalOpen,
}) => {
  const navigate = useNavigate();
  const [viewMore, setViewMore] = useState(false);
  const [viewMoreId, setViewMoreId] = useState(null);

  const handleViewMoreClick = (value) => {
    if (viewMoreId === value) {
      setViewMoreId(-1);
      setViewMore(false);
    } else {
      setViewMoreId(value);
      setViewMore(true);
    }
  };

  if (!modules?.data?.results?.length) {
    return (
      <Grid container>
        <NoDataAvailable
          imgHeight={4}
          imgWidth={4.5}
          message="No Modules Available"
        />
      </Grid>
    );
  }

  return (
    <>
      {modules.data.results.map((mod, index) => (
        <ModuleRow
          key={index}
          mod={mod}
          bootcamp={bootcamp}
          index={index}
          expandBootcampId={expandBootcampId}
          isHr={isHr}
          isBootcampConcluded={isBootcampConcluded}
          handleDeleteModalOpen={handleDeleteModalOpen}
          handleDissociateModalOpen={handleDissociateModalOpen}
          handleEditBootcampModule={handleEditBootcampModule}
          handleListAssessments={handleListAssessments}
          handleAssociateBootcampModalOpen={handleAssociateBootcampModalOpen}
          viewMore={viewMore}
          viewMoreId={viewMoreId}
          handleViewMoreClick={handleViewMoreClick}
          navigate={navigate}
        />
      ))}
    </>
  );
};

export default BootcampModuleExpand;
